import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getPreferences } from 'services/concierge.service';
import { AppNotification, ConnectionState, LoadingStates } from 'typings';

interface AppState {
  isAuthed: boolean;
  connectionState: ConnectionState;
  darkMode: boolean;
  notification: AppNotification | null;
  loadingState: LoadingStates;
  soundEnabled: boolean;
  distributionVolume: number;
  updateAvailable: boolean;
  currentlyPlayingProviderRecordingId: string | null;
}

function getAppState() {
  const appPreferences = getPreferences();

  return {
    isAuthed: false,
    connectionState: ConnectionState.INITIALIZED,
    darkMode: appPreferences.darkMode || false,
    notification: null,
    loadingState: LoadingStates.NOT_LOADED,
    soundEnabled: appPreferences.soundEnabled || false,
    distributionVolume: appPreferences.distributionVolume || 1,
    updateAvailable: false,
    currentlyPlayingProviderRecordingId: null,
  };
}

const initialAppState: AppState = getAppState();

export const appSlice = createSlice({
  name: 'app',
  initialState: initialAppState,
  reducers: {
    setIsAuthed: (state, action: PayloadAction<boolean>) => {
      state.isAuthed = action.payload;
    },
    setConnectionState: (state, action: PayloadAction<ConnectionState>) => {
      state.connectionState = action.payload;
    },
    setDarkMode: (state, action: PayloadAction<boolean>) => {
      state.darkMode = action.payload;
    },
    setNotification: (state, action: PayloadAction<AppNotification | null>) => {
      state.notification = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loadingState = action.payload ? LoadingStates.LOADING : LoadingStates.LOADED;
    },
    setSoundEnabled: (state, action: PayloadAction<boolean>) => {
      state.soundEnabled = action.payload;
    },
    setUpdateAvailable: (state, action: PayloadAction<boolean>) => {
      state.updateAvailable = action.payload;
    },
    setCurrentlyPlayingProviderRecordingId: (state, action) => {
      state.currentlyPlayingProviderRecordingId = action.payload;
    },
  },
});

export const {
  setIsAuthed,
  setConnectionState,
  setDarkMode,
  setNotification,
  setLoading,
  setSoundEnabled,
  setUpdateAvailable,
  setCurrentlyPlayingProviderRecordingId,
} = appSlice.actions;

export default appSlice.reducer;
